import { Arrow, Group, Line, Rect, Text } from "react-konva";
import {
  LEGACY_DEFAULT_COLOR,
  SHARED_MIN_ZONE_SIZE,
  SHARED_STROKE_WIDTH_DEFAULT,
  type SharedShape,
} from "@/lib/canvas/shared-shape";
import { pointToPx, sizeToPx, type Dims } from "@/lib/canvas/coords";

/**
 * Render a single SharedShape as a react-konva node.
 *
 * Used by both surfaces: tactical board view/editor AND telestrator
 * canvas. The same prop contract — `{ shape, selected, dims, onSelect }` —
 * works for every shared shape kind (arrow / line / zone / label /
 * freehand). Surface-specific kinds (player/ball/spotlight/dim/
 * formation) get their own renderers in their respective canvas
 * components; this file owns ONLY the shared set.
 *
 * Rendering notes:
 *   - Selection: bumps stroke width by 1.5px and overrides color
 *     with the legacy amber (#fbbf24), matching the legacy painter's
 *     "selected shape is always visually obvious regardless of stored
 *     color" behavior (review.js:461-472).
 *   - Default color when shape.color is undefined: LEGACY_DEFAULT_COLOR
 *     (#fde047). Stored shapes without a color field — common before
 *     Phase 6d-2 — render in the legacy yellow.
 *   - Default stroke width: SHARED_STROKE_WIDTH_DEFAULT (3).
 *   - Arrowhead: react-konva's <Arrow> handles head rendering. We pass
 *     `pointerLength` / `pointerWidth` proportional to stroke width.
 *   - Zone: dashed stroke + ~18% alpha fill derived from the stroke
 *     color (matches legacy line 451). Optional caption rendered with
 *     a translucent pill background.
 *   - Label: text with a translucent dark pill background. Auto-sizes
 *     to text content (Konva's <Text> does the measurement for us;
 *     we wrap in a <Group> with a <Rect> behind for the pill).
 *   - Freehand: <Line> with `tension: 0.5` for smoothing and
 *     `lineCap/lineJoin: "round"` for clean ends.
 */

const SELECTED_COLOR = "#fbbf24";
const SELECTION_BUMP = 1.5;
const ZONE_FILL_ALPHA = 0.18;
const LABEL_PADDING = 6;

/** Convert a hex color like "#38bdf8" into an rgba string with the
 * given alpha. Falls through to a translucent gray if the input
 * isn't a 6-digit hex. */
function hexToRgba(hex: string, alpha: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return `rgba(148, 163, 184, ${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export type ShapeRendererProps = {
  shape: SharedShape;
  /** Selection ring + stroke bump when true. */
  selected?: boolean;
  /** Stage dimensions in pixels (for unit→px conversion). */
  dims: Dims;
  /** Click handler — fires on the rendered Konva node. The shape's
   * id (if any) is provided via the closure; consumer typically calls
   * `selectShape(shape.id)`. */
  onSelect?: () => void;
  /** Optional draggable behavior. The canvas editor sets this true
   * when the armed tool is "select" and the shape is selected. */
  draggable?: boolean;
  /** Drag-end callback. Receives the new unit-coord shape after the
   * drag completes. The canvas component handles committing to the
   * scene. */
  onDragEnd?: (shape: SharedShape) => void;
};

export function ShapeRenderer(props: ShapeRendererProps) {
  const { shape, selected, dims, onSelect, draggable, onDragEnd } = props;
  const baseColor = shape.color ?? LEGACY_DEFAULT_COLOR;
  const color = selected ? SELECTED_COLOR : baseColor;
  const strokeWidth =
    (shape.strokeWidth ?? SHARED_STROKE_WIDTH_DEFAULT) +
    (selected ? SELECTION_BUMP : 0);

  // Every shape's root Konva node carries `id={shape.id}` so the
  // editor's Transformer-attach effect can find it via
  // `stage.findOne(`#${id}`)` AND `handleTransformEnd` can read
  // `node.id()` back. Without this id, both surfaces' Transformer
  // logic silently no-ops (PR #6 review-fix).
  const nodeId = shape.id;

  switch (shape.kind) {
    case "arrow": {
      const a = pointToPx({ x: shape.x1, y: shape.y1 }, dims);
      const b = pointToPx({ x: shape.x2, y: shape.y2 }, dims);
      return (
        <Arrow
          id={nodeId}
          points={[a.x, a.y, b.x, b.y]}
          stroke={color}
          fill={color}
          strokeWidth={strokeWidth}
          pointerLength={Math.max(8, strokeWidth * 3)}
          pointerWidth={Math.max(6, strokeWidth * 2.5)}
          lineCap="round"
          lineJoin="round"
          onClick={onSelect}
          onTap={onSelect}
          draggable={draggable}
          onDragEnd={(e) => {
            if (!onDragEnd) return;
            const dx = e.target.x() / dims.width;
            const dy = e.target.y() / dims.height;
            e.target.position({ x: 0, y: 0 });
            onDragEnd({
              ...shape,
              x1: clamp01(shape.x1 + dx),
              y1: clamp01(shape.y1 + dy),
              x2: clamp01(shape.x2 + dx),
              y2: clamp01(shape.y2 + dy),
            });
          }}
        />
      );
    }

    case "line": {
      const a = pointToPx({ x: shape.x1, y: shape.y1 }, dims);
      const b = pointToPx({ x: shape.x2, y: shape.y2 }, dims);
      return (
        <Line
          id={nodeId}
          points={[a.x, a.y, b.x, b.y]}
          stroke={color}
          strokeWidth={strokeWidth}
          lineCap="round"
          lineJoin="round"
          onClick={onSelect}
          onTap={onSelect}
          draggable={draggable}
          onDragEnd={(e) => {
            if (!onDragEnd) return;
            const dx = e.target.x() / dims.width;
            const dy = e.target.y() / dims.height;
            e.target.position({ x: 0, y: 0 });
            onDragEnd({
              ...shape,
              x1: clamp01(shape.x1 + dx),
              y1: clamp01(shape.y1 + dy),
              x2: clamp01(shape.x2 + dx),
              y2: clamp01(shape.y2 + dy),
            });
          }}
        />
      );
    }

    case "zone": {
      // Zones are positioned via a `<Group x={tl} y={tl}>` with a
      // `<Rect>` filling the group at (0, 0). The Transformer attaches
      // to the Group, which scales BOTH the rect AND the inner text
      // proportionally during a resize. `handleTransformEnd` reads
      // `node.width()` / `node.height()` from the Group; Konva tracks
      // these explicitly on Groups when set as props.
      const tl = pointToPx({ x: shape.x, y: shape.y }, dims);
      const sz = sizeToPx({ w: shape.w, h: shape.h }, dims);
      return (
        <Group
          id={nodeId}
          x={tl.x}
          y={tl.y}
          width={sz.w}
          height={sz.h}
          onClick={onSelect}
          onTap={onSelect}
          draggable={draggable}
          onDragEnd={(e) => {
            if (!onDragEnd) return;
            // Clamp the origin so a minimum-size zone always fits on
            // the pitch, then keep w/h at >= SHARED_MIN_ZONE_SIZE.
            // Without the `Math.max`, dragging a zone to the right /
            // bottom edge shrank it below the 0.02 floor that
            // `clampSharedShape` and the Transformer resize both
            // enforce — an inconsistent sliver zone.
            const newX = clamp01(
              Math.min(
                e.target.x() / dims.width,
                1 - SHARED_MIN_ZONE_SIZE,
              ),
            );
            const newY = clamp01(
              Math.min(
                e.target.y() / dims.height,
                1 - SHARED_MIN_ZONE_SIZE,
              ),
            );
            onDragEnd({
              ...shape,
              x: newX,
              y: newY,
              w: Math.min(Math.max(shape.w, SHARED_MIN_ZONE_SIZE), 1 - newX),
              h: Math.min(Math.max(shape.h, SHARED_MIN_ZONE_SIZE), 1 - newY),
            });
          }}
        >
          <Rect
            x={0}
            y={0}
            width={sz.w}
            height={sz.h}
            stroke={color}
            strokeWidth={strokeWidth}
            dash={[Math.max(8, strokeWidth * 3), Math.max(6, strokeWidth * 2)]}
            fill={hexToRgba(baseColor, ZONE_FILL_ALPHA)}
          />
          {shape.text ? (
            <Text
              x={LABEL_PADDING}
              y={LABEL_PADDING}
              text={shape.text}
              fontSize={14}
              fontStyle="700"
              fontFamily="ui-sans-serif, system-ui, sans-serif"
              fill={color}
              listening={false}
            />
          ) : null}
        </Group>
      );
    }

    case "label": {
      const p = pointToPx({ x: shape.x, y: shape.y }, dims);
      // Approximate text width for the pill — Konva's <Text> measures
      // exactly but we need a number for the <Rect> behind it. Use a
      // pixel-per-char heuristic; refined via a ref+measure pattern
      // later if labels start clipping.
      const approxTextWidth = Math.max(40, shape.text.length * 8);
      return (
        <Group
          id={nodeId}
          x={p.x}
          y={p.y}
          onClick={onSelect}
          onTap={onSelect}
          draggable={draggable}
          onDragEnd={(e) => {
            if (!onDragEnd) return;
            const newX = clamp01(e.target.x() / dims.width);
            const newY = clamp01(e.target.y() / dims.height);
            // Convert back to anchor-relative
            onDragEnd({ ...shape, x: newX, y: newY });
          }}
        >
          <Rect
            x={-LABEL_PADDING}
            y={-LABEL_PADDING}
            width={approxTextWidth + LABEL_PADDING * 2}
            height={18 + LABEL_PADDING * 2}
            cornerRadius={6}
            fill="rgba(15, 23, 42, 0.78)"
            stroke={color}
            strokeWidth={Math.max(1, strokeWidth - 1)}
          />
          <Text
            text={shape.text}
            fontSize={14}
            fontStyle="700"
            fontFamily="ui-sans-serif, system-ui, sans-serif"
            fill={color}
          />
        </Group>
      );
    }

    case "freehand": {
      // Flatten point list to Konva's [x1, y1, x2, y2, ...] format.
      const flat: number[] = [];
      for (const pt of shape.points) {
        const px = pointToPx(pt, dims);
        flat.push(px.x, px.y);
      }
      return (
        <Line
          id={nodeId}
          points={flat}
          stroke={color}
          strokeWidth={strokeWidth}
          lineCap="round"
          lineJoin="round"
          tension={0.5}
          onClick={onSelect}
          onTap={onSelect}
          draggable={draggable}
          onDragEnd={(e) => {
            if (!onDragEnd) return;
            const dx = e.target.x() / dims.width;
            const dy = e.target.y() / dims.height;
            e.target.position({ x: 0, y: 0 });
            onDragEnd({
              ...shape,
              points: shape.points.map((pt) => ({
                x: clamp01(pt.x + dx),
                y: clamp01(pt.y + dy),
              })),
            });
          }}
        />
      );
    }
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}
